import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { authAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { UserRegisterData } from '../types'

interface RegisterFormData {
  username: string
  password: string
  confirmPassword: string
}

export default function RegisterPage() {
  const navigate = useNavigate()
  const { setToken, setUser } = useAuthStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { register, handleSubmit, formState: { errors } } = useForm<RegisterFormData>({
    defaultValues: { username: '', password: '', confirmPassword: '' },
  })

  const onSubmit = async (data: RegisterFormData) => {
    if (data.password !== data.confirmPassword) {
      toast.error('两次输入的密码不一致')
      return
    }
    setIsSubmitting(true)
    try {
      const payload: UserRegisterData = { username: data.username.trim(), password: data.password }
      const response = await authAPI.register(payload)
      setToken(response.access_token)
      setUser(await authAPI.getCurrentUser())
      toast.success('注册成功，即将分配模拟用户偏好')
      navigate('/scoring')
    } catch (error: any) {
      const detail = error.response?.data?.detail
      const message = Array.isArray(detail)
        ? detail.map((item: any) => `${item?.loc?.at(-1) || '请求'}：${item?.msg || '格式不正确'}`).join('；')
        : String(detail || '注册失败，请重试')
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-slate-100 px-4 py-12">
      <div className="mx-auto max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-2xl text-white shadow-lg">✦</div>
          <h1 className="text-3xl font-bold text-slate-900">创建标注账号</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">只需用户名和密码。首次开始评分前，系统会为本账号抽取并展示一位稳定的模拟用户风格偏好。</p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">用户名</label>
            <input autoComplete="username" {...register('username', { required: '请输入用户名', minLength: { value: 3, message: '用户名至少 3 个字符' }, maxLength: { value: 50, message: '用户名最多 50 个字符' }, pattern: { value: /^[a-zA-Z0-9_\-\u4e00-\u9fff]+$/, message: '仅支持中文、字母、数字、下划线和连字符' } })} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" placeholder="用于之后继续标注" />
            {errors.username && <p className="mt-2 text-xs text-red-600">{errors.username.message}</p>}
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">密码</label>
            <input type="password" autoComplete="new-password" {...register('password', { required: '请输入密码', minLength: { value: 6, message: '密码至少 6 个字符' } })} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" placeholder="至少 6 个字符" />
            {errors.password && <p className="mt-2 text-xs text-red-600">{errors.password.message}</p>}
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">确认密码</label>
            <input type="password" autoComplete="new-password" {...register('confirmPassword', { required: '请再次输入密码', validate: (value, form) => value === form.password || '两次输入的密码不一致' })} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" placeholder="再次输入密码" />
            {errors.confirmPassword && <p className="mt-2 text-xs text-red-600">{errors.confirmPassword.message}</p>}
          </div>
          <button disabled={isSubmitting} className="w-full rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60">{isSubmitting ? '注册中…' : '注册并开始标注'}</button>
          <p className="text-center text-sm text-slate-600">已有账号？ <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-700">登录继续标注</Link></p>
        </form>
      </div>
    </div>
  )
}
